import { useEffect, useMemo } from "react";
import { Zap, ArrowRight, Clock, Check } from "lucide-react";
import { ParentHoloButton } from "@/components/parent/shared/parentPrimitives";
import { useParentActions } from "@/hooks/useParentActions";
import type { ParentAnalyticsResult } from "@/hooks/useParentAnalytics";
import type { CommandStatus } from "@/lib/parent/commandMetrics";
import type { ParentPriority } from "@/lib/parent/currentPriority";

interface Props {
  childId: string;
  analytics: ParentAnalyticsResult;
  status: CommandStatus;
  priority: ParentPriority;
}

export default function PrimaryParentAction({ childId, analytics, status, priority }: Props) {
  const { items, create, updateStatus } = useParentActions(null);

  // Seed exactly one re-engage action when needed. Hook-level dedup
  // prevents duplicates against existing open / snoozed / recently-resolved cards.
  useEffect(() => {
    if (!childId) return;
    if (status.state !== "reengage") return;
    const existing = items.find(
      (it) => it.child_user_id === childId && it.source_type === "inactivity" && (it.status === "Suggested" || it.status === "Planned"),
    );
    if (existing) return;
    void create({
      child_user_id: childId,
      action_title: priority.action.title,
      action_reason: `Last active ${analytics.daysSinceLastActivity ?? "—"} days ago. ${priority.action.reason}`,
      conversation_prompt: priority.prompt.text,
      topic: priority.label,
      priority: "High",
      status: "Suggested",
      source_type: "inactivity",
      source_id: null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId, status.state, items.length]);

  // Pick the single top-priority open action for this child.
  const primary = useMemo(() => {
    const order = { High: 0, Medium: 1, Low: 2 } as const;
    return items
      .filter((it) => it.child_user_id === childId && (it.status === "Suggested" || it.status === "Planned"))
      .sort((a, b) => order[a.priority] - order[b.priority] || new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
  }, [items, childId]);

  const title = primary?.action_title ?? priority.action.title;
  const reason = primary?.action_reason ?? priority.action.reason;

  return (
    <section className="relative h-full overflow-hidden rounded-3xl border border-primary/20 bg-gradient-to-br from-primary/[0.07] via-card/70 to-secondary/[0.05] p-5 backdrop-blur-2xl shadow-[0_24px_60px_-30px_hsl(var(--primary)/0.55),inset_0_1px_0_0_hsl(var(--foreground)/0.08)]">
      <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-primary/15 blur-[70px]" />
      <div className="pointer-events-none absolute -bottom-24 -left-12 h-48 w-48 rounded-full bg-secondary/10 blur-3xl" />
      <div className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
      <div className="relative">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/15 text-primary ring-1 ring-primary/30">
            <Zap className="h-3.5 w-3.5" />
          </span>
          <div className="min-w-0">
            <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-primary/90">Recommended Parent Action</p>
            <p className="font-heading text-xs text-muted-foreground sm:text-[13px]">Updated as your teen's signal shifts.</p>
          </div>
        </div>

        <p className="mt-5 font-heading text-lg font-semibold tracking-tight text-foreground sm:text-xl leading-snug">{title}</p>
        <p className="mt-1.5 text-[11px] text-muted-foreground">
          Based on: {priority.label}{priority.betaIndex ? ` · Step ${priority.betaIndex} of 13` : ""}
        </p>
        <div className="mt-2 rounded-2xl border border-white/[0.05] bg-background/30 px-3.5 py-2.5">
          <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">Why now</p>
          <p className="mt-1 text-xs leading-relaxed text-foreground/90">{reason}</p>
        </div>

        <ParentHoloButton
          onClick={() => primary && updateStatus(primary.id, "Planned")}
          className="mt-4 justify-between"
          disabled={!primary}
        >
          Plan Session
          <ArrowRight className="h-4 w-4" />
        </ParentHoloButton>

        <div className="mt-3 flex items-center justify-center gap-5 text-[11px] text-muted-foreground">
          <button
            type="button"
            onClick={() => primary && updateStatus(primary.id, "Snoozed")}
            className="inline-flex items-center gap-1.5 hover:text-foreground"
            disabled={!primary}
          >
            <Clock className="h-3 w-3" /> Snooze
          </button>
          <button
            type="button"
            onClick={() => primary && updateStatus(primary.id, "Completed")}
            className="inline-flex items-center gap-1.5 hover:text-foreground"
            disabled={!primary}
          >
            <Check className="h-3 w-3" /> Complete
          </button>
        </div>
      </div>
    </section>
  );
}